/** Interactive website training tour steps — one entry per portal site. */
export const WEBSITE_TRAINING_TOUR_STEPS = {
  AWP: {
    intro:
      'This quick tour walks through each AWP tool on this page. Use Next to highlight each tab and learn what it does.',
    steps: [
      {
        tabLabel: 'Daily Logs',
        title: 'Daily Logs',
        body: 'Submit shift logs and notes for your AWP shift.'
      },
      {
        tabLabel: 'Vendor Check-In/Out',
        title: 'Vendor Check-In / Check-Out',
        body: 'Check in vendors with their name, company, and reason for visit. Check them out when they leave.'
      },
      {
        tabLabel: 'Bed Assignments',
        title: 'Bed Assignments',
        body: 'View and manage which clients are assigned to each bed.'
      },
      {
        tabLabel: 'Case Management',
        title: 'Case Management',
        body: 'Housed residents, case manager assignments, and case notes.'
      },
      {
        tabLabel: 'Package Signage',
        title: 'Package Signage',
        body: 'Log incoming packages and print signage for client pickup.'
      }
    ]
  },
  MRSC: {
    intro:
      'This tour highlights each MRSC tab. Follow along to see where daily work happens on this site.',
    steps: [
      {
        tabLabel: 'Daily Logs',
        title: 'Daily Logs',
        body: 'Submit MRSC shift logs and end-of-shift documentation.'
      },
      {
        tabLabel: 'Shift Post',
        title: 'Shift Post',
        body: 'Read and write handoff notes between shifts.'
      },
      {
        tabLabel: 'Active Clients',
        title: 'Active Clients',
        body: 'Manage bed assignments, view the client list, and track who is on site.'
      },
      {
        tabLabel: 'Visitors',
        title: 'Visitors',
        body: 'Log and manage visitors at MRSC.'
      },
      {
        tabLabel: 'Packages',
        title: 'Packages',
        body: 'Track incoming packages and package signage.'
      }
    ]
  },
  'Drop-In': {
    intro:
      'This tour shows the main Drop-In tools. Each step highlights a tab at the top of the page.',
    steps: [
      {
        tabLabel: 'Daily Logs',
        title: 'Daily Logs',
        body: 'Submit Drop-In shift logs for your shift.'
      },
      {
        tabLabel: 'Utilization',
        title: 'Utilization',
        body: 'Managers can review seated clients, overnight stays, Epic flags, and consent status for a selected day.'
      },
      {
        tabLabel: 'Active Clients',
        title: 'Active Clients',
        body: 'Manage seating assignments, case management, and client status on the floor.'
      }
    ]
  },
  Coronado: {
    intro:
      'This tour walks through Coronado housing tools. Use Next to highlight each tab.',
    steps: [
      { tabLabel: 'Housing Logs', title: 'Housing Logs', body: 'Daily housing shift documentation and logs.' },
      { tabLabel: 'Tenants', title: 'Tenants', body: 'Tenant occupancy, guest directory, and room details.' },
      { tabLabel: 'Case Management', title: 'Case Management', body: 'Your assigned clients, assessments, and case notes.' },
      { tabLabel: 'Key Audit', title: 'Key Audit', body: 'Key inventory checks and audits.' },
      { tabLabel: 'Laundry', title: 'Laundry', body: 'Laundry sign-ups and scheduling.' },
      { tabLabel: 'Visitors', title: 'Visitors', body: 'Visitor log and check-in.' },
      { tabLabel: 'Wellness', title: 'Wellness', body: 'Wellness checks for guests.' },
      { tabLabel: 'Rent', title: 'Rent', body: 'Rent tracking and late notices.' },
      { tabLabel: 'Packages', title: 'Packages', body: 'Package signage and tracking.' }
    ]
  },
  Eddy: {
    intro:
      'This tour walks through Eddy housing tools. Use Next to highlight each tab.',
    steps: [
      { tabLabel: 'Housing Logs', title: 'Housing Logs', body: 'Daily housing shift documentation and logs.' },
      { tabLabel: 'Tenants', title: 'Tenants', body: 'Tenant occupancy, guest directory, and room details.' },
      { tabLabel: 'Case Management', title: 'Case Management', body: 'Your assigned clients, assessments, and case notes.' },
      { tabLabel: 'Key Audit', title: 'Key Audit', body: 'Key inventory checks and audits.' },
      { tabLabel: 'Shift Checklist', title: 'Shift Checklist', body: 'End-of-shift checklist (Eddy only).' },
      { tabLabel: 'Laundry', title: 'Laundry', body: 'Laundry sign-ups and scheduling.' },
      { tabLabel: 'Visitors', title: 'Visitors', body: 'Visitor log and check-in.' },
      { tabLabel: 'Wellness', title: 'Wellness', body: 'Wellness checks for guests.' },
      { tabLabel: 'Rent', title: 'Rent', body: 'Rent tracking and late notices.' },
      { tabLabel: 'Packages', title: 'Packages', body: 'Package signage and tracking.' }
    ]
  },
  ETH: {
    intro: 'This tour highlights the ETH program tools on this page.',
    steps: [
      { tabLabel: 'Client List', title: 'Client List', body: 'Manage ETH program clients and their records.' },
      { tabLabel: 'Reportings', title: 'Reportings', body: 'Program reporting and metrics.' }
    ]
  },
  'Women\'s Flex': {
    intro: 'This tour highlights the Women\'s Flex program tools on this page.',
    steps: [
      { tabLabel: 'Client List', title: 'Client List', body: 'Manage Women\'s Flex clients and their records.' },
      { tabLabel: 'Reportings', title: 'Reportings', body: 'Program reporting and metrics.' }
    ]
  },
  MHOP: {
    intro:
      'This tour highlights MHOP tools. Some tabs may be hidden depending on your role.',
    steps: [
      { tabLabel: 'Caseload Tracker', title: 'Caseload Tracker', body: 'Active and inactive MHOP clients on your caseload.' },
      { tabLabel: 'Groups Report', title: 'Groups Report', body: 'Document group sessions and reporting.' },
      { tabLabel: 'Audit Log', title: 'Audit Log', body: 'Review MHOP activity (admin only).' },
      { tabLabel: 'Export', title: 'Export', body: 'Export caseload data.' }
    ]
  },
  'Client Profile': {
    intro:
      'This is a practice client profile — not a real person. Each step highlights one control and explains exactly what it does. Scroll anytime; use Next to continue.',
    introBullets: [
      'The blue outline shows the control being explained',
      'Read “What this does” for each highlighted item',
      'Click Finish on the last step to complete training'
    ],
    steps: [
      {
        tabLabel: 'Overview',
        title: 'Overview tab',
        body: 'Shows the main client record: identity at the top, details and services in the center, and quick-reference panels on the right.',
        tip: 'Start here for most day-to-day client work.'
      },
      {
        tabLabel: 'Notes',
        title: 'Notes tab',
        body: 'Opens the notes workspace where staff log interactions, observations, and follow-ups for this client.',
        tip: 'Managers and admins also see activity and profile change history on this tab.'
      },
      {
        tabLabel: 'Consents',
        title: 'Consents tab',
        body: 'Tracks required DPH and HIPAA consent forms for Drop-In and AWP clients — what is signed, missing, or expired.',
        tip: 'Use this before seating or enrolling a client when forms are required.'
      },
      {
        tabLabel: 'Overview',
        selector: '.client-profile-photo-wrap',
        scopeSelector: '.client-profile',
        title: 'Client photo',
        body: 'Displays the client\'s photo. Click the image to open a larger view. Managers and admins can upload or replace the photo from here.',
        tip: 'A photo helps staff quickly confirm they have the right profile on the floor.'
      },
      {
        tabLabel: 'Overview',
        selector: '.client-profile-name',
        scopeSelector: '.client-profile',
        title: 'Client name',
        body: 'Shows the client\'s legal name as stored in the system. Preferred name appears underneath when one is on file.',
        tip: 'Use preferred name when speaking with the client if they have one listed.'
      },
      {
        tabLabel: 'Overview',
        selector: '.client-profile-meta',
        scopeSelector: '.client-profile',
        title: 'Status, location & ID',
        body: 'The status badge shows where the client is in the program (Active, Waiting, Exited, etc.). Location is their current program site. The ID chip is the unique client identifier used across the portal.',
        tip: 'Status controls which actions (Refer, Exit, Ban) are available.'
      },
      {
        tabLabel: 'Overview',
        buttonLabel: 'Edit Profile',
        scopeSelector: '.client-profile',
        title: 'Edit Profile button',
        body: 'Opens a form to update demographics, contact information, program fields, dietary restrictions, and other profile details. Saves changes to this client\'s record.',
        tip: 'Every save is logged — managers can review changes in History.'
      },
      {
        tabLabel: 'Overview',
        buttonLabel: 'History',
        scopeSelector: '.client-profile',
        title: 'History button',
        body: 'Opens a log of profile changes: who edited what field and when. Used for audits and troubleshooting incorrect data.',
        tip: 'Visible to managers and admins only.'
      },
      {
        tabLabel: 'Overview',
        buttonLabel: 'Transfer',
        scopeSelector: '.client-profile',
        title: 'Transfer button',
        body: 'Starts a transfer to another CFSF program. Creates a transfer record and updates the client\'s status while the transfer is pending.',
        tip: 'The receiving program uses Accept Transfer to complete the move.'
      },
      {
        tabLabel: 'Overview',
        buttonLabel: 'Exit',
        scopeSelector: '.client-profile',
        title: 'Exit button',
        body: 'Records that the client has left services. Prompts for an exit reason and notes, then updates status to Exited.',
        tip: 'Use when someone completes or discontinues services — not for temporary absences.'
      },
      {
        tabLabel: 'Overview',
        selector: '.client-info-card',
        scopeSelector: '.client-profile',
        title: 'Client details card',
        body: 'Lists core profile fields: client ID, birthday, phone, email, and program-specific information for this location.',
        tip: 'Some fields are editable inline by managers; others open through Edit Profile.'
      },
      {
        tabLabel: 'Overview',
        buttonLabel: 'Record Services',
        scopeSelector: '.client-profile',
        title: 'Record Services button',
        body: 'Opens a form to log services you provided today (case management, housing navigation, etc.). Entries appear grouped by date below.',
        tip: 'Record services at end of shift or after each significant interaction.'
      },
      {
        tabLabel: 'Overview',
        selector: '.client-profile-training-services',
        scopeSelector: '.client-profile',
        title: 'Services provided list',
        body: 'Shows a history of logged services by day, including who recorded them and any notes attached to each entry.',
        tip: 'Use this to review what was already documented before adding duplicates.'
      },
      {
        tabLabel: 'Overview',
        buttonLabel: 'File incident report',
        scopeSelector: '.client-profile',
        title: 'File incident report button',
        body: 'Starts the incident report workflow for this client. Creates a linked Asana task and can log the incident in daily logs.',
        tip: 'Use for safety incidents, behavioral events, or anything requiring documentation.'
      },
      {
        tabLabel: 'Overview',
        selector: '.client-profile-training-tasks',
        scopeSelector: '.client-profile',
        title: 'Asana tasks list',
        body: 'Displays open Asana tasks tied to this client — name, due date, assignee, and a link to open the task in Asana.',
        tip: 'Check here for outstanding follow-ups assigned to your team.'
      },
      {
        tabLabel: 'Overview',
        selector: '.client-profile-training-case-managers',
        scopeSelector: '.client-profile',
        title: 'Case manager panel',
        body: 'Shows who is assigned as this client\'s case manager. Staff can add or remove themselves when permitted; managers assign others from here.',
        tip: 'Case manager assignment may trigger needs-assessment tracking at Drop-In.'
      },
      {
        tabLabel: 'Overview',
        selector: '.client-profile-training-services-needed',
        scopeSelector: '.client-profile',
        title: 'Services needed panel',
        body: 'Lists services this client still needs (for example, case management or housing navigation). Helps staff prioritize outreach.',
        tip: 'Only appears when services are flagged on the profile.'
      },
      {
        tabLabel: 'Notes',
        selector: '.client-profile-training-notes-search',
        scopeSelector: '.client-profile',
        title: 'Search notes',
        body: 'Filters the note list by text in the note body or the name of who wrote it. Useful on clients with long note histories.',
        tip: 'Search is instant — no need to press Enter.'
      },
      {
        tabLabel: 'Notes',
        selector: '.client-profile-training-notes-add',
        scopeSelector: '.client-profile',
        title: 'Add a note',
        body: 'Type a note in the text box and click Add to save it. The note is timestamped and attributed to your account automatically.',
        tip: 'Notes are visible to staff with access to this client\'s location.'
      },
      {
        tabLabel: 'Notes',
        selector: '.client-profile-training-notes-list',
        scopeSelector: '.client-profile',
        title: 'Note history',
        body: 'Shows all saved notes newest-first, with author name and date. Managers can delete notes if needed.',
        tip: 'This sample note is practice data — safe to explore on this demo profile.'
      },
      {
        tabLabel: 'Consents',
        selector: '.client-profile-training-consents-table',
        scopeSelector: '.client-profile',
        title: 'Required consents table',
        body: 'Lists each required form with status (signed, missing, expired), signature date, and expiration. Missing or expired forms show as warnings.',
        tip: 'Drop-In and AWP clients typically need DPH and HIPAA forms on file.'
      },
      {
        tabLabel: 'Consents',
        buttonLabel: 'Sign',
        scopeSelector: '.client-profile',
        title: 'Sign button',
        body: 'Opens the digital consent workflow so the client can sign on screen, or lets you upload a scanned paper form.',
        tip: 'Renew / Upload appears when a form was signed before but needs renewal.'
      },
      {
        tabLabel: 'Consents',
        selector: '.client-profile-training-consents-history',
        scopeSelector: '.client-profile',
        title: 'Document history',
        body: 'Archives every signed or uploaded consent PDF with date, signer, and download link for your records.',
        tip: 'Download copies here for audits or external requests.'
      }
    ]
  }
};

export const getWebsiteTrainingTourConfig = (location) =>
  WEBSITE_TRAINING_TOUR_STEPS[location] || null;
